import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { usePlatformSummary, usePlatformMsps } from '../../hooks/usePlatformData';
import { useApiClient } from '../../api/apiClient';
import { useUser } from '../../context/UserContext';
import { relativeTime, exactTime } from '../../utils/time';
import PlatformShell from '../../components/layout/PlatformShell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import ColorPicker from '../../components/ui/ColorPicker';
import { TH, TR, TD } from '../../components/ui/Table';
import PageError from '../../components/ui/PageError';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useUpdateBranding, clearBrandingCache } from '../../context/BrandingContext';
import { fetchBranding } from '../../auth/brandingClient';

function OnboardMspModal({ onClose, api, onSuccess }) {
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [tier, setTier] = useState('Standard');
  const [portalName, setPortalName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1a73e8');
  const [secondaryColor, setSecondaryColor] = useState('#ffffff');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!name || !ownerEmail || !ownerName) return;
    setSaving(true);
    try {
      await api.post('/platform/msps', {
        name, ownerEmail, ownerName,
        partnerTier: tier,
        portalName: portalName || undefined,
        primaryColor, secondaryColor, backgroundColor, textColor,
      });
      setResult({ ok: true, msg: `MSP onboarded successfully. Owner will receive an invitation email.` });
      onSuccess();
    } catch (err) {
      setResult({ ok: false, msg: err.message || 'Failed to create MSP' });
    } finally {
      setSaving(false);
    }
  };

  const iStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
    padding: '8px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 28, maxWidth: 520, width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Onboard New MSP</div>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>Create a new MSP partner organization on the platform.</div>

        {[
          { l: 'MSP Name', v: name, s: setName, p: 'Acme Security Services', req: true },
          { l: 'Owner Email', v: ownerEmail, s: setOwnerEmail, p: 'admin@acmesecurity.com', req: true },
          { l: 'Owner Name', v: ownerName, s: setOwnerName, p: 'John Smith', req: true },
          { l: 'Portal Name', v: portalName, s: setPortalName, p: 'Acme Command Portal' },
        ].map(f => (
          <div key={f.l} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{f.l}{f.req && ' *'}</div>
            <input value={f.v} onChange={e => f.s(e.target.value)} placeholder={f.p} style={iStyle} />
          </div>
        ))}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Partner Tier</div>
          <select value={tier} onChange={e => setTier(e.target.value)} style={{ ...iStyle, cursor: 'pointer' }}>
            <option value="Standard">Standard</option>
            <option value="Growth">Growth</option>
            <option value="Flagship">Flagship</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
          <ColorPicker label="Primary Color" value={primaryColor} onChange={setPrimaryColor} />
          <ColorPicker label="Secondary Color" value={secondaryColor} onChange={setSecondaryColor} />
        </div>

        {result && (
          <div style={{
            marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono,
            background: result.ok ? `${C.grn}12` : C.redBg,
            border: `1px solid ${result.ok ? C.grn : C.red}33`,
            color: result.ok ? C.grn : C.red,
          }}>{result.msg}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="sec" onClick={onClose}>{result?.ok ? 'Close' : 'Cancel'}</Button>
          {!result?.ok && (
            <Button variant="pri" onClick={handleSubmit} disabled={saving || !name || !ownerEmail || !ownerName}>
              {saving ? 'Creating...' : 'Create MSP'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const api = useApiClient();
  const { data: summary, loading: sumLoading, error: sumError } = usePlatformSummary();
  const {
    data: msps,
    loading: mspsLoading,
    refreshing: mspsRefreshing,
    isStale: mspsStale,
    error: mspsError,
    refetch,
  } = usePlatformMsps();
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [toggleError, setToggleError] = useState(null);
  const { profile } = useUser();
  const { startImpersonation } = useImpersonation();
  const updateBranding = useUpdateBranding();
  const mspsUnavailable = Boolean(mspsError) && !mspsLoading && msps.length === 0;
  const mspsStatusText = mspsUnavailable
    ? 'Unavailable'
    : mspsRefreshing
      ? `${msps.length} partners - refreshing`
      : mspsStale
        ? `${msps.length} partners - cached`
        : `${msps.length} partners`;
  const mspsErrorMeta = mspsError
    ? [
        mspsError.status ? `HTTP ${mspsError.status}` : null,
        mspsError.method && mspsError.path ? `${mspsError.method} ${mspsError.path}` : null,
        mspsError.requestId ? `Request ${mspsError.requestId}` : null,
      ].filter(Boolean).join(' | ')
    : '';

  const handleImpersonate = async (mspId, mspName) => {
    startImpersonation(mspId, mspName);
    clearBrandingCache();
    const branding = await fetchBranding({ mspId });
    updateBranding(branding);
    navigate('/msp/dashboard');
  };

  const handleToggleStatus = async (msp) => {
    const id = msp.mspId || msp.id;
    const newStatus = msp.status === 'Active' || msp.status === 'active' ? 'inactive' : 'active';
    setTogglingId(id);
    try {
      await api.put(`/platform/msps/${id}/status`, { status: newStatus });
      refetch();
    } catch (err) {
      setToggleError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const inactiveMsps = summary ? (summary.totalMsps ?? 0) - (summary.activeMsps ?? 0) : 0;
  const kpis = [
    { l: 'Total MSPs', v: summary?.totalMsps ?? '\u2014', sub: summary ? `${summary.activeMsps ?? 0} active / ${inactiveMsps} inactive` : '', c: C.sky },
    { l: 'Total Clients', v: summary?.totalClients ?? '\u2014', sub: 'Across all MSPs', c: C.pur },
    { l: 'Total Findings', v: summary?.totalFindings ?? '\u2014', sub: 'Platform-wide', c: C.red },
    { l: 'Average Score', v: summary?.avgScoreAcrossAllClients ?? summary?.averageScore ?? '\u2014', sub: 'All clients', c: C.grn },
    { l: 'MSPs This Month', v: summary?.mspsAddedThisMonth ?? '\u2014', sub: 'New onboardings', c: C.amb },
    { l: 'Scans This Month', v: summary?.scansRunThisMonth ?? summary?.scansThisMonth ?? '\u2014', sub: 'Total runs', c: C.org },
  ];

  const orgName = profile?.organizationName || profile?.companyName || 'Platform';
  return (
    <PlatformShell title={`${orgName} Platform Dashboard`} sub={`${orgName} \u2014 Platform Overview`}>
      {sumError && <PageError error={sumError} />}
      {toggleError && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Failed to update status: {toggleError}</span>
          <button onClick={() => setToggleError(null)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>{'\u2715'}</button>
        </div>
      )}
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10, marginBottom: 14 }}>
        {kpis.map(k => (
          <Card key={k.l} style={{ padding: '14px 16px', borderTop: `2px solid ${k.c}` }}>
            {sumLoading ? <Skeleton height={40} /> : <>
              <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>{k.l}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: k.c, fontFamily: FONTS.mono, lineHeight: 1 }}>{k.v}</div>
              <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>{k.sub}</div>
            </>}
          </Card>
        ))}
      </div>

      {/* MSP Table */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Button variant="sec" onClick={() => navigate('/msp/dashboard')} style={{ fontSize: 11 }}>
          {'\u2190'} MSP Dashboard
        </Button>
        <Button variant="pri" onClick={() => setOnboardOpen(true)}>+ Onboard New MSP</Button>
      </div>

      <Card>
        <CardHeader title="MSP Partners" action={<span style={{ fontSize: 9, color: mspsUnavailable ? C.amb : (mspsRefreshing || mspsStale) ? C.sky : C.t3, fontFamily: FONTS.mono }}>{mspsStatusText}</span>} />
        {!mspsUnavailable && (mspsRefreshing || mspsStale) && (
          <div style={{ margin: '0 16px 12px', padding: '8px 10px', borderRadius: 6, background: `${C.sky}10`, border: `1px solid ${C.sky}25`, color: C.t2, fontFamily: FONTS.mono, fontSize: 10 }}>
            Showing the last loaded MSP partner list while the platform refreshes the directory in the background.
          </div>
        )}
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <TH cols={['MSP Name', 'Partner Tier', 'Clients', 'Avg Score', 'Total Findings', 'Status', 'Last Activity', '']} />
          <tbody>
            {mspsLoading && [0, 1, 2].map(i => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                {[120, 70, 40, 40, 50, 55, 80, 100].map((w, j) => (
                  <TD key={j}><Skeleton width={w} height={12} /></TD>
                ))}
              </tr>
            ))}
            {mspsUnavailable && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', fontFamily: FONTS.mono }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.amb, marginBottom: 6 }}>
                  MSP partner list is temporarily unavailable.
                </div>
                <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.5, marginBottom: 12 }}>
                  Platform metrics loaded, but the partner directory API returned an error. Retry after the service recovers.
                  {mspsErrorMeta && <span style={{ display: 'block', marginTop: 4, color: C.t3 }}>{mspsErrorMeta}</span>}
                </div>
                <Button variant="sec" style={{ fontSize: 10 }} onClick={refetch}>Retry partner list</Button>
              </td></tr>
            )}
            {!mspsUnavailable && !mspsLoading && msps.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: C.t3, fontFamily: FONTS.mono, fontSize: 12 }}>
                No MSP partners yet. Click "Onboard New MSP" to get started.
              </td></tr>
            )}
            {!mspsUnavailable && msps.map(m => {
              const id = m.mspId || m.id || '';
              const name = m.name || m.companyName || '';
              const tier = m.partnerTier || m.tier || 'Standard';
              const clients = m.clientCount ?? m.clients ?? 0;
              const avgScore = m.avgScore ?? m.averageScore ?? null;
              const findings = m.totalOpenFindings ?? m.totalFindings ?? m.findings ?? 0;
              const isActive = typeof m.isActive === 'boolean' ? m.isActive : (m.status === 'Active' || m.status === 'active');
              const status = isActive ? 'Active' : 'Inactive';
              const lastActivity = m.lastScanAt || m.lastActivity || m.lastLogin || null;
              const tierColor = tier === 'Flagship' ? '#9333ea' : tier === 'Growth' ? '#ca8a04' : '#2563eb';
              const scoreColor = typeof avgScore === 'number' ? (avgScore >= 80 ? C.grn : avgScore >= 60 ? C.amb : C.red) : C.t3;

              return (
                <TR key={id}>
                  <TD><div onClick={() => handleImpersonate(id, name)} style={{ fontSize: 12, fontWeight: 600, color: C.sky, cursor: 'pointer' }}>{name}</div></TD>
                  <TD><span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 4, fontFamily: FONTS.mono, color: tierColor, background: `${tierColor}14`, border: `1px solid ${tierColor}33` }}>{tier}</span></TD>
                  <TD style={{ fontSize: 12, fontWeight: 700, color: C.t1, fontFamily: FONTS.mono }}>{clients}</TD>
                  <TD><span style={{ fontSize: 14, fontWeight: 800, color: scoreColor, fontFamily: FONTS.mono }}>{typeof avgScore === 'number' ? avgScore : '\u2014'}</span></TD>
                  <TD style={{ fontSize: 12, fontWeight: 600, color: findings > 0 ? C.red : C.t3, fontFamily: FONTS.mono }}>{findings}</TD>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? C.grn : C.t3 }} />
                      <Tag label={status} color={isActive ? C.grn : C.t3} />
                    </div>
                  </TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }} title={lastActivity ? exactTime(lastActivity) : ''}>
                    {lastActivity ? relativeTime(lastActivity) : '\u2014'}
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <Button variant="pri" style={{ fontSize: 10 }} onClick={() => navigate(`/platform/msps/${id}`)}>Edit</Button>
                      <Button variant="sec" style={{ fontSize: 10 }} disabled={togglingId === id} onClick={() => handleToggleStatus(m)}>
                        {isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </table>
      </Card>

      {onboardOpen && <OnboardMspModal onClose={() => setOnboardOpen(false)} api={api} onSuccess={() => refetch()} />}
    </PlatformShell>
  );
}
